import './polyfills'
import { getElementTag } from '@nrk/dh-server-libs/get-element-tag'
import Forklaringsscroll from './components/forklaringsscroll'
import StegvisVideo from '@nrk/dh-basis-stegvis-video'
import LevendeStillbilde from '@nrk/dh-basis-levende-stillbilde'
import Spesialmodul from './components/media/spesialmodul'
import { Schemas, setupTracker } from './lib/snowplow'

import './client.module.css'
import './components/forklaringsscroll.module.css'
import cardStyles from './components/cards/cards.module.css'

const tag = getElementTag(__APP_NAME__, __APP_VERSION__)

function safeDefine(elementName, constructor) {
  if (typeof customElements.get(elementName) === 'undefined') {
    customElements.define(elementName, constructor)
  }
}

export class AppElement extends HTMLElement {
  name = tag
  #observer

  async connectedCallback() {
    this.addEventListener('dh-basis-scrollhistorie-trigger-activated', this.#handleTriggerActivated)

    this.#loadCustomElements()
    this.#startCompletionLogger()

    this.trackSnowplowEvent = setupTracker('dh-forklaringsscroll', {
      host: this.dataset.snowplowHost,
      selfId: this.dataset.selfId || undefined,
    })
  }

  disconnectedCallback() {
    this.removeEventListener(
      'dh-basis-scrollhistorie-trigger-activated',
      this.#handleTriggerActivated,
    )
    this.#stopCompletionLogger()
  }

  async #loadCustomElements() {
    safeDefine('dh-forklaringsscroll', Forklaringsscroll)
    safeDefine('dh-fs-basis-stegvis-video', StegvisVideo)
    safeDefine('dh-fs-basis-levende-stillbilde', LevendeStillbilde)
    safeDefine('dh-fs-spesialmodul', Spesialmodul)

    /* Stegvis lottie is only defined if used */
    if (
      this.features.lottie &&
      typeof customElements.get('dh-fs-basis-stegvis-lottie') === 'undefined'
    ) {
      const stegvisLottie = (
        await import('@nrk/dh-basis-stegvis-lottie' /* webpackChunkName: "dh-stegvis-lottie" */)
      ).default
      safeDefine('dh-fs-basis-stegvis-lottie', stegvisLottie)
    }

    if (this.features.kart && typeof customElements.get('dh-fs-basis-kart') === 'undefined') {
      const kart = (await import('./lib/karthistorie-chunk' /* webpackChunkName: "dh-kart" */))
        .default

      safeDefine('dh-fs-basis-kart', kart)
    }
  }

  #handleTriggerActivated = (e) => {
    /** @type {import('@snowplow/tracker-core').SelfDescribingJson<import('./lib/snowplow').StepEvent>} */
    const event = {
      schema: Schemas.StepEvent,
      data: { current: e.detail.triggerIndex + 1, total: this.#cards.length },
    }
    this.trackSnowplowEvent(event)
  }

  #startCompletionLogger() {
    let hasStarted = false
    let hasCompleted = false

    // eslint-disable-next-line compat/compat
    this.#observer = new IntersectionObserver(
      (intersections) => {
        let isIntersecting = intersections[0]?.isIntersecting
        if (!hasStarted && isIntersecting && this.getBoundingClientRect().top > 0) {
          hasStarted = true
          /** @type {import('@snowplow/tracker-core').SelfDescribingJson<import('./lib/snowplow').ViewportEvent>} */
          const event = {
            schema: Schemas.ViewportEvent,
            data: { kind: 'entry' },
          }
          this.trackSnowplowEvent(event)
        } else if (
          hasStarted &&
          !hasCompleted &&
          !isIntersecting &&
          this.getBoundingClientRect().bottom < 0
        ) {
          hasCompleted = true
          /** @type {import('@snowplow/tracker-core').SelfDescribingJson<import('./lib/snowplow').ViewportEvent>} */
          const event = {
            schema: Schemas.ViewportEvent,
            data: { kind: 'exit' },
          }
          this.trackSnowplowEvent(event)
        }
      },
      { threshold: [0] },
    )
    this.#observer.observe(this)
  }

  #stopCompletionLogger() {
    this.#observer.disconnect()
  }

  get features() {
    return JSON.parse(this.getAttribute('features') || '{}')
  }

  get #cards() {
    return this.querySelectorAll(`.${cardStyles.card}`)
  }
}

safeDefine(tag, AppElement)
